<template>
	<app-content class="h-panel">
		<div class="h-panel-bar"><span class="h-panel-title">币别设置</span></div>
		<div class="margin-top margin-left">
			<Button color="primary" @click="openCreate">新增</Button>
			<Button class="margin-left" @click="syncPublicRates" :loading="syncLoading">同步公开汇率</Button>
		</div>
		<div class="h-panel-body">
			<Table :datas="datas" :border="true">
				<TableItem title="币别" prop="code"></TableItem>
				<TableItem title="名称" prop="name"></TableItem>
				<TableItem title="汇率" prop="exchangeRate"></TableItem>
				<TableItem title="是否本位币">
					<template slot-scope="{data}">
						{{data.localCurrency?'是':'否'}}
					</template>
				</TableItem>
				<TableItem title="操作" :width="100" align="center">
					<div class="actions" slot-scope="{data}" v-if="!data.localCurrency">
						<span @click="edit(data)">编辑</span>
						<span @click="remove(data)">删除</span>
					</div>
				</TableItem>
			</Table>
		</div>
		<Modal v-model="showForm" type="drawer-right" hasCloseIcon>
			<div slot="header">币别设置</div>
			<Form ref="form" v-width="400" :labelWidth="100" :model="form" :rules="validationRules">
				<FormItem single label="">
					<Checkbox v-model="customCurrency">手动输入自定义币种</Checkbox>
				</FormItem>
				<FormItem label="币别" prop="code">
					<Select
						v-if="!customCurrency"
						v-model="form.code"
						:datas="currencyOptions"
						:deletable="false"
						keyName="key"
						titleName="title"
						@change="handleCurrencyChange"></Select>
					<input
						v-else
						type="text"
						v-model="form.code"
						maxlength="3"
						placeholder="如 MXN"
						@blur="handleCustomCodeBlur">
				</FormItem>
				<FormItem label="名称" prop="name">
					<input type="text" v-model="form.name" :readonly="!customCurrency">
				</FormItem>
				<FormItem label="汇率" prop="exchangeRate">
					<NumberInput v-model="form.exchangeRate"></NumberInput>
					<div class="h-form-help" v-if="rateLoading">正在获取公开汇率...</div>
					<div class="h-form-help" v-else-if="rateSource">已自动带入公开汇率，来源：{{rateSource}}</div>
				</FormItem>
			</Form>
			<div class="text-center">
				<Button color="green" @click="submit" :loading="loading">{{form.id?'更新':'保存'}}</Button>
				<Button @click="showForm=false" :loading="loading">取消</Button>
			</div>
		</Modal>
	</app-content>
</template>

<script>
	const STANDARD_CURRENCIES = [
		{"key": "CNY", "title": "CNY - 人民币", "name": "人民币"},
		{"key": "USD", "title": "USD - 美元", "name": "美元"},
		{"key": "EUR", "title": "EUR - 欧元", "name": "欧元"},
		{"key": "GBP", "title": "GBP - 英镑", "name": "英镑"},
		{"key": "JPY", "title": "JPY - 日元", "name": "日元"},
		{"key": "CAD", "title": "CAD - 加元", "name": "加元"},
		{"key": "AUD", "title": "AUD - 澳元", "name": "澳元"},
	];

	const emptyForm = {
		"code": "",
		"name": "",
		"exchangeRate": "",
	};

	export default {
		name: 'Currency',
		data() {
			return {
				datas: [],
				form: Object.assign({}, emptyForm),
				customCurrency: false,
				validationRules: {
					required: ["code", "name", "exchangeRate"],
					rules: {
						code: {
							maxLen: 3,
							minLen: 3,
							valid: {
								pattern: /^[A-Z]{3}$/,
								message: '需为三位的大写英文字母'
							}
						}
					}
					},
					showForm: false,
					loading: false,
					syncLoading: false,
					rateLoading: false,
					rateSource: ''
				};
			},
		computed: {
			currencyOptions() {
				const usedCodes = new Set((this.datas || [])
					.map(item => item.code)
					.filter(code => code && code !== this.form.code));
				const options = STANDARD_CURRENCIES
					.filter(item => !usedCodes.has(item.key))
					.map(item => Object.assign({}, item));
				if (this.form.code && !options.some(item => item.key === this.form.code)) {
					options.unshift({
						key: this.form.code,
						title: `${this.form.code} - ${this.form.name || this.form.code}`,
						name: this.form.name || this.form.code
					});
				}
				return options;
			}
		},
		watch: {
			showForm(val) {
				if (!val) {
					this.reset();
				}
			},
			customCurrency(val) {
				if (!val) {
					const matched = this.findStandardCurrency(this.form.code);
					if (matched) {
						this.$set(this.form, 'name', matched.name);
					} else {
						this.$set(this.form, 'code', '');
						this.$set(this.form, 'name', '');
						this.$set(this.form, 'exchangeRate', '');
						this.rateSource = '';
					}
				}
			}
		},
		methods: {
			refreshFieldValidation(fields = []) {
				this.$nextTick(() => {
					if (!this.$refs.form || !this.$refs.form.validField) {
						return;
					}
					fields.forEach(field => {
						this.$refs.form.validField(field);
					});
				});
			},
			findStandardCurrency(code) {
				const normalizedCode = (code || '').toString().trim().toUpperCase();
				return STANDARD_CURRENCIES.find(item => item.key === normalizedCode);
			},
			sortCurrencies(list = []) {
				return [...list].sort((left, right) => {
					const leftLocal = left && left.localCurrency ? 1 : 0;
					const rightLocal = right && right.localCurrency ? 1 : 0;
					if (leftLocal !== rightLocal) {
						return rightLocal - leftLocal;
					}
					const leftCode = (left && left.code ? left.code : '').toString();
					const rightCode = (right && right.code ? right.code : '').toString();
					return leftCode.localeCompare(rightCode);
				});
			},
			openCreate() {
				this.reset();
				this.showForm = true;
			},
			loadList() {
				this.$api.setting.currency.list().then(({data}) => {
					this.datas = this.sortCurrencies(data || []);
				})
			},
			submit() {
				this.normalizeCurrencyForm();
				let validResult = this.$refs.form.valid();
				if (validResult.result) {
					this.loading = true;
					this.$api.setting.currency[this.form.id ? 'update' : 'save'](this.form).then(() => {
						this.loadList();
						this.showForm = false;
						this.loading = false;
					}).catch(() => {
						this.loading = false;
					})
				}
			},
				reset() {
					this.form = Object.assign({}, emptyForm)
					this.customCurrency = false;
					this.rateLoading = false;
					this.rateSource = '';
				},
			remove(data) {
				this.$Confirm("确认删除?").then(() => {
					this.$api.setting.currency.delete(data.id).then(() => {
						this.loadList();
					})
				})
			},
			edit(data) {
				this.form = Object.assign({}, emptyForm, data)
				this.customCurrency = !this.findStandardCurrency(data.code);
				this.rateSource = '';
				this.showForm = true;
			},
			normalizeCurrencyForm() {
				this.$set(this.form, 'code', (this.form.code || '').toString().trim().toUpperCase());
				const matched = this.findStandardCurrency(this.form.code);
				if (!this.customCurrency && matched) {
					this.$set(this.form, 'name', matched.name);
				} else {
					this.$set(this.form, 'name', (this.form.name || '').toString().trim());
				}
				this.refreshFieldValidation(['code', 'name']);
			},
			handleCurrencyChange(currency) {
				if (!currency) {
					return;
				}
				this.$set(this.form, 'code', currency.key || currency.code || '');
				this.$set(this.form, 'name', currency.name || '');
				this.$set(this.form, 'exchangeRate', '');
				this.fillPublicRate();
			},
			handleCustomCodeBlur() {
				this.normalizeCurrencyForm();
				if (/^[A-Z]{3}$/.test(this.form.code)) {
					const matched = this.findStandardCurrency(this.form.code);
					if (matched && !this.form.name) {
						this.$set(this.form, 'name', matched.name);
					}
					this.refreshFieldValidation(['code', 'name']);
					this.fillPublicRate();
				}
			},
			fillPublicRate() {
				if (!this.form.code) {
					return;
				}
				this.rateLoading = true;
				this.rateSource = '';
				this.$api.setting.currency.publicRate({code: this.form.code}).then(({data}) => {
					if (!this.form.name && data.currencyName) {
						this.$set(this.form, 'name', data.currencyName);
						this.refreshFieldValidation(['name']);
					}
					this.$set(this.form, 'exchangeRate', data.exchangeRate);
					this.rateSource = data.source || '';
				}).catch((error) => {
					this.rateSource = '';
					const message = (error && (error.msg || error.message)) || '自动获取公开汇率失败，请手工录入';
					this.$Message.warning(message);
				}).finally(() => {
					this.rateLoading = false;
				});
			},
			syncPublicRates() {
				this.syncLoading = true;
				this.$api.setting.currency.syncPublicRates().then(({data}) => {
					const sourceText = data.source ? `，来源：${data.source}` : '';
					this.$Message.success(`已同步 ${data.syncedCount || 0} 个币种的公开汇率${sourceText}`);
					this.loadList();
				}).finally(() => {
					this.syncLoading = false;
				});
			}
		},
		mounted() {
			this.loadList();
		}
	};
</script>
